import React from "react";
import { Box, Button, Typography } from "@mui/material";

import { formatMoneyCompact, metricLabel } from "./meteredFormat";
import { productLabel, skuLabel } from "./meteredLabels";

const ACTION_GREEN = "#26890d";

const chipSx = (active) => ({
  borderRadius: "16px",
  textTransform: "none",
  fontSize: "0.78rem",
  fontWeight: active ? 600 : 400,
  backgroundColor: active ? ACTION_GREEN : "#fff",
  color: active ? "#fff" : "#444",
  border: `1px solid ${active ? ACTION_GREEN : "#ccc"}`,
  px: 1.4,
  py: 0.3,
  minWidth: 0,
  "&:hover": { backgroundColor: active ? "#1f6e0a" : "#f0f0f0", border: `1px solid ${active ? "#1f6e0a" : "#bbb"}` },
});

const Chip = ({ label, amount, active, onClick, title }) => (
  <Button size="small" onClick={onClick} aria-pressed={active} title={title} sx={chipSx(active)}>
    {label}
    {amount !== undefined && (
      <Box component="span" sx={{ ml: 0.8, fontSize: "0.72rem", fontWeight: 500, opacity: active ? 0.9 : 0.65 }}>
        {amount}
      </Box>
    )}
  </Button>
);

// Pick a product — and then one of its SKUs — as chips, each with its amount for the chosen measure (Net / Gross /
// Discount), so you can see where the money is before filtering. Names are readable ("Code Quality", "Linux"); the raw
// id is what's sent to the API and kept in the URL. A product's SKU row only appears once it is picked.
const ProductSkuFilter = ({ products, product, sku, metric, onChange }) => {
  const list = products ?? [];
  const amountOf = (item) => formatMoneyCompact(item[metric]);
  const total = list.reduce((sum, p) => sum + (p[metric] ?? 0), 0);
  const selected = list.find((p) => p.product === product);
  // Keep a picked product / SKU visible even if it isn't in the (separately filtered) list right now.
  const productChips = product && !selected ? [...list, { product }] : list;
  const skuChips = selected?.skus ?? (sku ? [{ sku }] : []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }} role="group" aria-label="Product">
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", mr: 0.5, minWidth: 56 }}>Product</Typography>
        <Chip label="All products" amount={list.length ? formatMoneyCompact(total) : undefined} active={!product} onClick={() => onChange({ product: "", sku: "" })} />
        {productChips.map((p) => (
          <Chip
            key={p.product}
            label={productLabel(p.product)}
            title={p.product}
            amount={p[metric] !== undefined ? amountOf(p) : undefined}
            active={product === p.product}
            onClick={() => onChange({ product: p.product, sku: "" })}
          />
        ))}
      </Box>
      {product && skuChips.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }} role="group" aria-label="SKU">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", mr: 0.5, minWidth: 56 }}>SKU</Typography>
          <Chip label="All SKUs" active={!sku} onClick={() => onChange({ product, sku: "" })} />
          {skuChips.map((item) => (
            <Chip
              key={item.sku}
              label={skuLabel(item.sku, product)}
              title={item.sku}
              amount={item[metric] !== undefined ? amountOf(item) : undefined}
              active={sku === item.sku}
              onClick={() => onChange({ product, sku: item.sku })}
            />
          ))}
        </Box>
      )}
      <Typography sx={{ fontSize: "0.68rem", color: "#aaa", pl: "64px" }}>Amounts are {metricLabel(metric).toLowerCase()} cost for the period.</Typography>
    </Box>
  );
};

export default ProductSkuFilter;
